import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { capitalize, orderBy } from 'lodash';
import { renderBooleanValue } from '../../../../../util/miscellaneousUtils';
import {
  Button,
  Progress,
  Modal,
  Tooltip,
  Checkbox,
  Spin,
  Typography
} from 'antd';
import { LockFilled } from '@ant-design/icons';
import {
  RadioButttonWithIcons,
  getAlertRadioOptions
} from '../../../../../components/ns/RadioButttonWithIcons';
import styles from './index.scss';
import { checkAccess } from '../../../../common/utils/index';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCommentAltLines,
  faEllipsisH,
  faSearch
} from '@fortawesome/pro-regular-svg-icons';
import FurtherInformation from './FurtherInformation';
import RelatedPartyModal from './RelatedPartyModal';

const resourceCode = 'APP_3_RESOURCE_UI_AMLS_COMMON_ALERTS';
const { Text } = Typography;

export const NSAlertDetailHits = ({
  GS,
  data,
  apiActions,
  authentication,
  totalDiff,
  selectedForCompare,
  onSelectForComparision,
  hitsUserFeedback,
  alertLevels,
  individualCorporateType = 'I'
}) => {
  const [isModalVisible, setModalVibility] = useState(false);
  const [showRelatedPartiesModal, setShowRelatedPartiesModal] = useState({});
  const [selectedHitId, setSelectedHitId] = useState(null);
  // Track per-hit expansion for related-party linked list
  const [expandedLinked, setExpandedLinked] = useState({});
  const toggleLinkedExpand = (hitId) =>
    setExpandedLinked((prev) => ({ ...prev, [hitId]: !prev?.[hitId] }));
  const setHitId = (hitId) => {
    setSelectedHitId(hitId);
    setModalVibility(true);
  };

  const isCorporate = individualCorporateType === 'C';
  const columnsToRemove = isCorporate
    ? ['Identification Number', 'Identification Number Match']
    : ['Registered Number', 'Registered Number Match'];

  GS = {
    ...GS,
    widgetConfig: GS?.widgetConfig?.filter(
      ({ columnName }) => !columnsToRemove.includes(columnName)
    )
  };

  const renderDataByMultipleKey = (
    column,
    record,
    elementType = '',
    fromData = null
  ) => {
    let keys = column.itemName.split('|');
    const dataRecord = { ...record };
    if (keys.length >= 2 && record !== undefined) {
      let renderCount = 0;
      return keys.map((key, index) => {
        if (
          (key == 'partyIdNo' || key == 'registeredNumber') &&
          fromData === 'hitsData'
        ) {
          dataRecord[key] = null;
        }
        if (key == 'partyKey' && fromData === 'hitsData') {
          dataRecord[key] = null;
        }
        if (dataRecord[key]) renderCount = 1;
        if (renderCount === 1) {
          return elementType === 'TD' ? (
            <td key={index}>
              {dataRecord[key] ? <p>{dataRecord[key]}</p> : null}
            </td>
          ) : (
            <>
              {key == 'watchlistIdentificationNumber' && dataRecord[key] ? (
                <p key={index} id={fromData} className={styles.lockedField}>
                  <span>
                    {dataRecord[key] !== null && dataRecord[key] !== 'null'
                      ? dataRecord[key]
                      : '--'}
                  </span>
                  {dataRecord['isHardRule'] && (
                    <LockFilled className={styles.lockIcon} />
                  )}
                </p>
              ) : (
                <p key={index} id={fromData}>
                  {dataRecord[key] ? dataRecord[key] : null}
                </p>
              )}
            </>
          );
        } else if (renderCount === 0 && index === keys.length - 1)
          return elementType === 'TD' ? (
            <td key={index}>
              {dataRecord[key] ? <p>{dataRecord[key]}</p> : '--'}
            </td>
          ) : (
            <p key={index}>{dataRecord[key] ? dataRecord[key] : '--'}</p>
          );
      });
    } else return null;
  };

  const onUserFeedbackChangeHandler = (obj, record) => {
    const param = {
      alertId: data.alertId,
      alertSourceId: data.alertSourceId,
      watchlistId: record.watchlistId,
      newAlertStatus: obj,
      hitId: record.hitId
    };
    apiActions.postNSHitsUserFeedback(param);
  };

  const addToCompare = (hitId) => {
    onSelectForComparision(hitId);
  };

  const renderCustomTableColumn = (column, record) => {
    const { isWhitelisted: isWhitelistedGlobally = false } = data;
    switch (column?.itemName) {
      case 'isWhitelisted':
        return (
          <td key={`v-${record.alertId}`}>
            {isWhitelistedGlobally == true
              ? ' True (Global) '
              : renderBooleanValue(record[column?.itemName])}
          </td>
        );
      case 'reviewFeedback':
        return (
          <td key={record.hitId}>
            {checkAccess(authentication, resourceCode, 'Update') ? (
              <Spin
                wrapperClassName={styles.hitSpin}
                spinning={
                  hitsUserFeedback?.isLoading &&
                  hitsUserFeedback?.hitIds?.includes(record.hitId)
                }>
                <RadioButttonWithIcons
                  options={getAlertRadioOptions(alertLevels)}
                  defaultValue={record[column.itemName]?.toString()}
                  onChangeHandler={(res) =>
                    onUserFeedbackChangeHandler(res, record)
                  }
                />
              </Spin>
            ) : (
              '--'
            )}
          </td>
        );
      case 'hitScore':
        return (
          <>
            <div className={styles.flex} key={record.alertId}>
              <div>
                <span>
                  {record.hitLevelLabel || capitalize(record.hitLevel)}
                </span>
                <br />
                <Tooltip title={record[column.itemName]}>
                  <span>{record[column.itemName]?.toFixed(2).toString()}</span>
                </Tooltip>
                {record.hitScore && (
                  <Progress
                    percent={record.hitScore}
                    strokeColor={record.hitLevelColor}
                    showInfo={false}
                  />
                )}
              </div>
            </div>
            <Checkbox
              style={{ marginRight: '15px' }}
              checked={selectedForCompare.some((s) => s === record.hitId)}
              onChange={() => addToCompare(record.hitId)}
            />
          </>
        );
      case 'furtherInformation':
        return (
          <td key={record.alertId}>
            {record.furtherInformation ? (
              <div className={styles.furtherInformation} key={record.hitId}>
                <div>
                  <FontAwesomeIcon
                    icon={faEllipsisH}
                    style={{ fontSize: '11px', marginRight: '6px' }}
                  />
                  <FontAwesomeIcon
                    icon={faCommentAltLines}
                    style={{ fontSize: '20px' }}
                    onClick={() => {
                      setHitId(record.hitId);
                    }}
                  />
                </div>
                {record.furtherInformation && selectedHitId == record.hitId && (
                  <Modal
                    visible={isModalVisible}
                    onCancel={() => setModalVibility(false)}
                    title={<Text strong>Further Information</Text>}
                    width="60%"
                    footer={null}
                    wrapClassName={styles.furtherInfoModal}>
                    <FurtherInformation hitId={record.hitId} />
                  </Modal>
                )}
              </div>
            ) : (
              '--'
            )}
          </td>
        );
    }
    return null;
  };

  const getNestedTableColumns = (
    columnDetail,
    rowData,
    TYPE,
    fromData = null
  ) => {
    // Special-case: Render Related Party fields (3 left, 1 right) in a single row
    // when these four itemNames are present in the same grouped column.
    try {
      const allDetail = columnDetail || [];
      const itemNames = allDetail.map((c) => c.itemName);
      const visByName = Object.fromEntries(
        allDetail.map((c) => [c.itemName, !!c.visibility])
      );
      const allHidden = [
        'relatedPartyName',
        'relatedPartyWId',
        'relatedPartyRelation',
        'relatedPartyWC'
      ].every((k) => visByName[k] === false);
      const isRelatedPartyGroup = [
        'relatedPartyName',
        'relatedPartyWId',
        'relatedPartyRelation',
        'relatedPartyWC'
      ].every((n) => itemNames.includes(n));

      // Render custom RP header for grouped columns as well, respecting per-item visibility
      if (TYPE === 'HEADING' && isRelatedPartyGroup) {
        const sorted = orderBy(allDetail, ['itemIndex'], ['asc']);
        const byName = (n) => sorted.find((c) => c.itemName === n);
        const nameObj = byName('relatedPartyName');
        const idObj = byName('relatedPartyWId');
        const relObj = byName('relatedPartyRelation');
        const catObj = byName('relatedPartyWC');
        return (
          <div className={styles.rpHeader} key="rp-header-grouped">
            <div className={styles.rpHeaderLeft}>
              <span className={styles.rpHeaderText}>
                {nameObj?.visibility ? nameObj?.itemLabel : ''}
              </span>
              <span className={styles.rpHeaderText}>
                {idObj?.visibility ? idObj?.itemLabel : ''}
              </span>
              <span className={styles.rpHeaderText}>
                {relObj?.visibility ? relObj?.itemLabel : ''}
              </span>
            </div>
            <div className={styles.rpHeaderRight}>
              <span className={styles.rpHeaderText}>
                {catObj?.visibility ? catObj?.itemLabel : ''}
              </span>
            </div>
          </div>
        );
      }

      if (TYPE === 'DATA' && fromData === 'hitsData' && isRelatedPartyGroup) {
        const sorted = orderBy(allDetail, ['itemIndex'], ['asc']);
        const byName = (n) => sorted.find((c) => c.itemName === n);

        // Render a value with ellipsis and show Tooltip only if overflowing
        const EllipsisText = ({ pKey, value }) => {
          const display = value ?? '--';
          const ref = useRef(null);
          const [overflow, setOverflow] = useState(false);

          useEffect(() => {
            const el = ref.current;
            if (el) {
              // Check overflow
              const isOverflow = el.scrollWidth > el.clientWidth;
              setOverflow(isOverflow);
            }
          }, [display]);

          const para = (
            <p
              ref={ref}
              key={pKey}
              className={styles.rpText}
              style={{ wordBreak: 'break-all' }}>
              {display}
            </p>
          );

          if (overflow) {
            return (
              <Tooltip title={display} mouseEnterDelay={0.3} key={`${pKey}-tt`}>
                {para}
              </Tooltip>
            );
          }
          return para;
        };

        const renderValueP = (key, value) => (
          <EllipsisText pKey={key} value={value} />
        );

        // Try to render as a list from linkedTo when available
        let linked = null;
        const rawLinked = rowData && rowData.linkedTo;
        if (rawLinked) {
          if (Array.isArray(rawLinked)) {
            linked = rawLinked;
          } else if (typeof rawLinked === 'string') {
            try {
              const parsed = JSON.parse(rawLinked);
              if (Array.isArray(parsed)) linked = parsed;
            } catch (e) {
              // ignore parse errors; will fallback
            }
          }
        }

        if (Array.isArray(linked) && linked.length > 0) {
          // Render each linked item vertically: 4 lines stacked
          // Default show 1, with a View more / View less control and count
          const hitKey = rowData?.hitId;
          const total = linked.length;
          const isExpanded = !!expandedLinked?.[hitKey];
          const DEFAULT_COLLAPSED = 10;
          const visible = isExpanded
            ? linked
            : linked.slice(0, DEFAULT_COLLAPSED);
          const remaining = Math.max(total - DEFAULT_COLLAPSED, 0);

          return (
            <div className={styles.rpListWrapper}>
              <div className={styles.rpList}>
                {visible.map((lnk, idx) => (
                  <div
                    className={styles.rpItem}
                    key={`related-party-item-${idx}`}>
                    <div className={styles.rpItemLeft}>
                      {visByName['relatedPartyName'] &&
                        renderValueP('relatedPartyName', lnk?.name || lnk?.id)}
                      {visByName['relatedPartyWId'] &&
                        renderValueP('relatedPartyWId', lnk?.id)}
                      {visByName['relatedPartyRelation'] &&
                        renderValueP('relatedPartyRelation', lnk?.relation)}
                    </div>
                    <div className={styles.rpItemRight}>
                      <div className={styles.rpWC}>
                        {visByName['relatedPartyWC'] &&
                          renderValueP('relatedPartyWC', lnk?.category)}
                        {!allHidden && (
                          <Tooltip title="View Details">
                            <FontAwesomeIcon
                              icon={faSearch}
                              className={styles.rpZoomIcon}
                              role="img"
                              aria-label="zoom"
                              onClick={() => {
                                setShowRelatedPartiesModal({
                                  visible: true,
                                  hitDetails: rowData,
                                  watchlistId: lnk?.id
                                });
                              }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {!allHidden && (
                <div className={styles.rpFooter}>
                  <span className={styles.rpCount}>Total: {total}</span>
                  {total > DEFAULT_COLLAPSED && (
                    <Button
                      type="text"
                      size="small"
                      className={styles.rpToggle}
                      onClick={() => toggleLinkedExpand(hitKey)}>
                      {isExpanded ? 'View less' : `View more (${remaining})`}
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        }

        // No related parties found for this hit
        if (!allHidden) {
          return (
            <div className={styles.rpListWrapper} key="related-party-empty">
              <div className={styles.rpList}>
                <div className={`${styles.rpItem} ${styles.rpEmpty}`}>
                  <span className={styles.rpEmptyText}>
                    No related parties found
                  </span>
                </div>
              </div>
            </div>
          );
        }
        return null;
      }
    } catch (e) {
      // Fail open to the default rendering below if any unexpected issue occurs
    }
    return orderBy(columnDetail, ['itemIndex'], ['asc'])
      .filter((column) => column.visibility)
      .map((column, idx) => {
        const columnKey = `${column.itemName}-${idx}`;

        if (TYPE === 'HEADING') {
          return <p key={columnKey}>{column.itemLabel}</p>;
        }

        if (
          (column.itemName === 'hitLevel' || column.itemName === 'hitScore') &&
          rowData.hits !== undefined
        ) {
          return null;
        }

        if (
          renderCustomTableColumn(column, rowData) ||
          column.itemName === 'hitLevel'
        ) {
          return (
            <React.Fragment key={columnKey}>
              {renderCustomTableColumn(column, rowData)}
            </React.Fragment>
          );
        }

        if (renderDataByMultipleKey(column, rowData)) {
          return (
            <React.Fragment key={columnKey}>
              {renderDataByMultipleKey(column, rowData, TYPE, fromData)}
            </React.Fragment>
          );
        }
        const columnValue = rowData[column.itemName];
        // Corner case handling for null/undefined, 0, empty string, or invalid data
        const isValidNumber =
          typeof columnValue === 'number' && !isNaN(columnValue);
        const displayValue =
          columnValue === 0
            ? '0.00'
            : isValidNumber
            ? columnValue.toFixed(2)
            : columnValue;

        const isScoreOrWeight =
          column.itemName.toLowerCase().includes('score') ||
          column.itemName.toLowerCase().includes('weight');
        const tooltipTitle =
          isScoreOrWeight && columnValue !== undefined
            ? isValidNumber
              ? columnValue.toString()
              : columnValue
            : '--';
        if (isScoreOrWeight) {
          return (
            <Tooltip key={columnKey} title={tooltipTitle}>
              <p
                key={columnKey}
                style={{
                  color:
                    (rowData[column.itemName] == 100 ||
                      rowData[column.itemName] == 1) &&
                    column.itemName.toLowerCase().includes('score')
                      ? '#DF6057'
                      : '#556069',
                  ...(columnDetail?.length > 2 && { flexBasis: '46px' }),
                  textAlign: 'start',
                  wordBreak: 'break-all',
                  cursor: 'pointer'
                }}>
                {columnValue || columnValue === 0 ? displayValue : '--'}
              </p>
            </Tooltip>
          );
        } else {
          return (
            <p
              key={columnKey}
              style={{
                maxWidth: '22ch',
                wordBreak: 'break-all'
              }}>
              {columnValue ?? '--'}
            </p>
          );
        }
      });
  };

  const getAlertViewHitsHeading = (GS) => {
    let renderHeading = null;
    const sortedSettings = orderBy(
      GS.widgetConfig,
      ['columnIndex'],
      ['asc']
    ).filter((column) => column.columnVisibility);

    // Build a lookup of all items across the entire widgetConfig once
    const allDetails = (GS?.widgetConfig || []).flatMap(
      (s) => s?.columnDetail || []
    );
    const rpByName = Object.fromEntries(allDetails.map((c) => [c.itemName, c]));

    const consumed = {};
    renderHeading = sortedSettings.map((setting, id) => {
      if (consumed[id]) return null;
      return (
        <React.Fragment key={id}>
          {setting.columnDetail.length === 0 ? (
            <div className={styles.separatorBar}>{setting.columnName}</div>
          ) : (
            (() => {
              // Attempt to coalesce four single-detail RP columns into one header row
              const relatedItems = [
                'relatedPartyName',
                'relatedPartyWId',
                'relatedPartyRelation',
                'relatedPartyWC'
              ];
              const isSingleDetail = setting.columnDetail.length === 1;
              const singleItem = isSingleDetail
                ? setting.columnDetail[0].itemName
                : null;
              if (
                isSingleDetail &&
                singleItem &&
                relatedItems.includes(singleItem)
              ) {
                const collected = [setting];
                const needed = relatedItems.filter((n) => n !== singleItem);
                for (
                  let j = id + 1;
                  j < sortedSettings.length && collected.length < 4;
                  j++
                ) {
                  const s = sortedSettings[j];
                  if (
                    s.columnDetail.length === 1 &&
                    relatedItems.includes(s.columnDetail[0].itemName) &&
                    needed.includes(s.columnDetail[0].itemName)
                  ) {
                    collected.push(s);
                    consumed[j] = true;
                    const idx = needed.indexOf(s.columnDetail[0].itemName);
                    if (idx > -1) needed.splice(idx, 1);
                  }
                }
                if (collected.length === 4) {
                  const groupedColumnDetail = orderBy(
                    collected.flatMap((c) => c.columnDetail),
                    ['itemIndex'],
                    ['asc']
                  );
                  const visByName = Object.fromEntries(
                    groupedColumnDetail.map((c) => [c.itemName, !!c.visibility])
                  );
                  const allHidden = relatedItems.every(
                    (k) => visByName[k] === false
                  );
                  if (allHidden) return null;
                  return (
                    <>
                      <tr
                        key={`rp-h-${id}`}
                        style={{ height: '57px', alignItems: 'flex-start' }}>
                        <th className={styles.spaceBetween}>
                          {getNestedTableColumns(
                            groupedColumnDetail,
                            undefined,
                            'HEADING'
                          )}
                        </th>
                        <th className={styles.spaceBetween}>
                          {getNestedTableColumns(
                            groupedColumnDetail,
                            GS?.data || {},
                            'DATA'
                          )}
                        </th>
                      </tr>
                      <tr
                        key={`rp-h-empty-${id}-1`}
                        style={{ height: '57px', alignItems: 'flex-start' }}>
                        <th className={styles.spaceBetween}>
                          <div className={styles.rpHeader}>
                            <div className={styles.rpHeaderLeft}>
                              <span className={styles.rpHeaderText}></span>
                              <span className={styles.rpHeaderText}></span>
                              <span className={styles.rpHeaderText}></span>
                            </div>
                            <div className={styles.rpHeaderRight}>
                              <span className={styles.rpHeaderText}></span>
                            </div>
                          </div>
                        </th>
                        <th className={styles.spaceBetween}></th>
                      </tr>
                      <tr
                        key={`rp-h-empty-${id}-2`}
                        style={{ height: '57px', alignItems: 'flex-start' }}>
                        <th className={styles.spaceBetween}>
                          <div className={styles.rpHeader}>
                            <div className={styles.rpHeaderLeft}>
                              <span className={styles.rpHeaderText}></span>
                              <span className={styles.rpHeaderText}></span>
                              <span className={styles.rpHeaderText}></span>
                            </div>
                            <div className={styles.rpHeaderRight}>
                              <span className={styles.rpHeaderText}></span>
                            </div>
                          </div>
                        </th>
                        <th className={styles.spaceBetween}></th>
                      </tr>
                      <tr
                        key={`rp-h-empty-${id}-3`}
                        style={{ height: '57px', alignItems: 'flex-start' }}>
                        <th className={styles.spaceBetween}>
                          <div className={styles.rpHeader}>
                            <div className={styles.rpHeaderLeft}>
                              <span className={styles.rpHeaderText}></span>
                              <span className={styles.rpHeaderText}></span>
                              <span className={styles.rpHeaderText}></span>
                            </div>
                            <div className={styles.rpHeaderRight}>
                              <span className={styles.rpHeaderText}></span>
                            </div>
                          </div>
                        </th>
                        <th className={styles.spaceBetween}></th>
                      </tr>
                    </>
                  );
                }
                // Fall through to default rendering when less than 4 RP singles gathered
              }
              const rpKeys = [
                'relatedPartyName',
                'relatedPartyWId',
                'relatedPartyRelation',
                'relatedPartyWC'
              ];
              const hasRP = setting.columnDetail.some((c) =>
                rpKeys.includes(c.itemName)
              );
              // If this header row is a grouped RP section and all four RP fields are hidden, skip the entire row
              if (setting.columnDetail.length >= 2) {
                const names = setting.columnDetail.map((c) => c.itemName);
                const hasAllRP = rpKeys.every((n) => names.includes(n));
                if (hasAllRP) {
                  const visByName = Object.fromEntries(
                    setting.columnDetail.map((c) => [
                      c.itemName,
                      !!c.visibility
                    ])
                  );
                  const allHidden = rpKeys.every((k) => visByName[k] === false);
                  if (allHidden) {
                    return null;
                  }
                }
              }
              return (
                <tr
                  key={id}
                  style={{
                    height: hasRP ? '57px' : undefined,
                    alignItems: hasRP ? 'flex-start' : undefined
                  }}>
                  {setting.columnDetail.length >= 2 ? (
                    <React.Fragment>
                      <th className={styles.spaceBetween} key={id}>
                        {getNestedTableColumns(
                          setting.columnDetail,
                          undefined,
                          'HEADING'
                        )}
                      </th>
                      <th className={styles.spaceBetween} key={id + 1}>
                        {getNestedTableColumns(
                          setting.columnDetail,
                          data,
                          'DATA'
                        )}
                      </th>
                    </React.Fragment>
                  ) : (
                    (() => {
                      const rpKeys = [
                        'relatedPartyName',
                        'relatedPartyWId',
                        'relatedPartyRelation',
                        'relatedPartyWC'
                      ];
                      const sortedSingles = orderBy(
                        setting.columnDetail,
                        ['itemIndex'],
                        ['asc']
                      );
                      const visibleSingles = sortedSingles.filter(
                        (c) => c.visibility
                      );
                      const firstVisibleRP = visibleSingles.find((c) =>
                        rpKeys.includes(c.itemName)
                      );
                      return visibleSingles.map((column, idx) => {
                        if (
                          firstVisibleRP &&
                          column.itemName === firstVisibleRP.itemName
                        ) {
                          const nameObj = rpByName['relatedPartyName'];
                          const watchlistIdNoObj = rpByName['relatedPartyWId'];
                          const relationshipObj =
                            rpByName['relatedPartyRelation'];
                          const watchlistCategoryObj =
                            rpByName['relatedPartyWC'];
                          const allHidden = [
                            nameObj,
                            watchlistIdNoObj,
                            relationshipObj,
                            watchlistCategoryObj
                          ].every((o) => o && o.visibility === false);
                          if (allHidden) {
                            return null;
                          }
                          return (
                            <React.Fragment key={`rp-header-${idx}`}>
                              <th className={styles.spaceBetween}>
                                <div className={styles.rpHeader}>
                                  <div className={styles.rpHeaderLeft}>
                                    <span className={styles.rpHeaderText}>
                                      {nameObj?.visibility
                                        ? nameObj?.itemLabel
                                        : ''}
                                    </span>
                                    <span className={styles.rpHeaderText}>
                                      {watchlistIdNoObj?.visibility
                                        ? watchlistIdNoObj?.itemLabel
                                        : ''}
                                    </span>
                                    <span className={styles.rpHeaderText}>
                                      {relationshipObj?.visibility
                                        ? relationshipObj?.itemLabel
                                        : ''}
                                    </span>
                                  </div>
                                  <div className={styles.rpHeaderRight}>
                                    <span className={styles.rpHeaderText}>
                                      {watchlistCategoryObj?.visibility
                                        ? watchlistCategoryObj?.itemLabel
                                        : ''}
                                    </span>
                                  </div>
                                </div>
                              </th>
                              <th />
                            </React.Fragment>
                          );
                        } else if (rpKeys.includes(column.itemName)) {
                          // For other RP single-detail columns, do not render placeholders when all four are hidden
                          const nameObj = rpByName['relatedPartyName'];
                          const watchlistIdNoObj = rpByName['relatedPartyWId'];
                          const relationshipObj =
                            rpByName['relatedPartyRelation'];
                          const watchlistCategoryObj =
                            rpByName['relatedPartyWC'];
                          const allHidden = [
                            nameObj,
                            watchlistIdNoObj,
                            relationshipObj,
                            watchlistCategoryObj
                          ].every((o) => o && o.visibility === false);
                          if (allHidden) return null;
                          return (
                            <React.Fragment key={idx}>
                              <th> </th>
                              <th></th>
                            </React.Fragment>
                          );
                        }

                        return (
                          <React.Fragment key={idx}>
                            <th className={styles.bold}>
                              {' '}
                              {column.itemLabel}{' '}
                            </th>
                            <th>
                              {renderDataByMultipleKey(column, data)
                                ? renderDataByMultipleKey(column, data)
                                : data[column.itemName]
                                ? data[column.itemName]
                                : column.itemName === 'reviewFeedback'
                                ? ''
                                : '--'}
                            </th>
                          </React.Fragment>
                        );
                      });
                    })()
                  )}
                </tr>
              );
            })()
          )}
        </React.Fragment>
      );
    });

    return renderHeading;
  };

  const getAlertViewHitsData = (GS) => {
    let renderData = null;
    const sortedSettings = orderBy(
      GS.widgetConfig,
      ['columnIndex'],
      ['asc']
    ).filter((column) => column.columnVisibility);

    renderData = data?.hits?.map((item, itemId) => {
      return (
        <div
          key={itemId}
          className={`${
            selectedForCompare.some((s) => s === item.hitId)
              ? styles.selected
              : ''
          }`}>
          {(() => {
            const relatedItems = [
              'relatedPartyName',
              'relatedPartyWId',
              'relatedPartyRelation',
              'relatedPartyWC'
            ];
            const consumed = new Array(sortedSettings.length).fill(false);
            const rows = [];

            for (let id = 0; id < sortedSettings.length; id++) {
              if (consumed[id]) continue;
              const setting = sortedSettings[id];
              const isDiffValue = totalDiff.some(
                (s) =>
                  setting.columnDetail.some((c) =>
                    c.itemName.split('|').includes(s)
                  ) && selectedForCompare.some((s) => s === item.hitId)
              );
              // Attempt to coalesce four separate related-party columns into one row
              const isSingleDetail = setting.columnDetail.length === 1;
              const singleItem = isSingleDetail
                ? setting.columnDetail[0].itemName
                : null;
              if (
                isSingleDetail &&
                singleItem &&
                relatedItems.includes(singleItem)
              ) {
                // Collect next three settings that belong to the related set
                const collected = [setting];
                const needed = relatedItems.filter((n) => n !== singleItem);
                for (
                  let j = id + 1;
                  j < sortedSettings.length && collected.length < 4;
                  j++
                ) {
                  const s = sortedSettings[j];
                  if (
                    s.columnDetail.length === 1 &&
                    relatedItems.includes(s.columnDetail[0].itemName) &&
                    needed.includes(s.columnDetail[0].itemName)
                  ) {
                    collected.push(s);
                    // mark as consumed and remove from needed
                    const idx = needed.indexOf(s.columnDetail[0].itemName);
                    if (idx > -1) needed.splice(idx, 1);
                    consumed[j] = true;
                  }
                }

                if (collected.length === 4) {
                  // Build a synthetic grouped columnDetail to leverage existing renderer
                  const groupedColumnDetail = orderBy(
                    collected.flatMap((c) => c.columnDetail),
                    ['itemIndex'],
                    ['asc']
                  );
                  const visByName = Object.fromEntries(
                    groupedColumnDetail.map((c) => [c.itemName, !!c.visibility])
                  );
                  const allHidden = [
                    'relatedPartyName',
                    'relatedPartyWId',
                    'relatedPartyRelation',
                    'relatedPartyWC'
                  ].every((k) => visByName[k] === false);
                  if (allHidden) {
                    continue;
                  }
                  rows.push(
                    <tr
                      key={`rp-${id}`}
                      className={`${isDiffValue ? 'diff' : ''} ${
                        styles.rpParent
                      } ${
                        setting.columnDetail.length === 0
                          ? styles.seperator
                          : ''
                      }`}>
                      {(() => {
                        // Determine if the current hit has any related parties
                        const rawLinked = item && item.linkedTo;
                        let hasLinked = false;
                        if (Array.isArray(rawLinked)) {
                          hasLinked = rawLinked.length > 0;
                        } else if (typeof rawLinked === 'string') {
                          try {
                            const parsed = JSON.parse(rawLinked);
                            hasLinked =
                              Array.isArray(parsed) && parsed.length > 0;
                          } catch (e) {
                            hasLinked = false;
                          }
                        }
                        const tdClassName = [
                          styles.autoHeight,
                          !hasLinked && styles.emptyAuto
                        ]
                          .filter(Boolean)
                          .join(' ');
                        return (
                          <td className={tdClassName}>
                            {getNestedTableColumns(
                              groupedColumnDetail,
                              item,
                              'DATA',
                              'hitsData'
                            )}
                          </td>
                        );
                      })()}
                    </tr>
                  );
                  continue;
                }
                // If not all found, fall through to default rendering for current setting
              }

              // Default row rendering (including potential multi-detail RP group)
              // If this setting is an RP grouped column and all RP items are hidden, skip the row
              {
                const names = setting.columnDetail.map((c) => c.itemName);
                const isRPGroup = [
                  'relatedPartyName',
                  'relatedPartyWId',
                  'relatedPartyRelation',
                  'relatedPartyWC'
                ].every((n) => names.includes(n));
                let shouldSkip = false;
                if (isRPGroup) {
                  const visByName = Object.fromEntries(
                    setting.columnDetail.map((c) => [
                      c.itemName,
                      !!c.visibility
                    ])
                  );
                  const allHidden = [
                    'relatedPartyName',
                    'relatedPartyWId',
                    'relatedPartyRelation',
                    'relatedPartyWC'
                  ].every((k) => visByName[k] === false);
                  shouldSkip = allHidden;
                }
                if (shouldSkip) {
                  // skip pushing this row entirely
                } else {
                  rows.push(
                    <tr
                      key={id}
                      className={`${isDiffValue ? 'diff' : ''} ${
                        setting.columnDetail.length === 0
                          ? styles.seperator
                          : ''
                      }`}>
                      {orderBy(setting?.columnDetail, ['itemIndex'], ['asc'])
                        .filter((column) => column.visibility)
                        .map((column, idx) => {
                          if (setting.columnDetail.length >= 2) {
                            if (column.itemIndex === 0) {
                              const tdClassName = isRPGroup
                                ? `${styles.autoHeight}`
                                : `${styles.spaceBetween}`;
                              return (
                                <td className={tdClassName} key={idx}>
                                  {getNestedTableColumns(
                                    setting.columnDetail,
                                    item,
                                    'DATA',
                                    'hitsData'
                                  )}
                                </td>
                              );
                            } else return null;
                          } else if (
                            renderCustomTableColumn(column, item) !== null
                          )
                            return renderCustomTableColumn(column, item);
                          else if (
                            renderDataByMultipleKey(column, item, 'TD') !== null
                          )
                            return renderDataByMultipleKey(column, item, 'TD');
                          else {
                            const { itemName } = column || {};
                            const displayKey =
                              itemName === 'partyIdNo' ||
                              itemName === 'registeredNumber'
                                ? 'watchlistIdentificationNumber'
                                : itemName;

                            const value = item?.[displayKey] || '--';

                            return (
                              <td key={idx}>
                                <p>{value}</p>
                              </td>
                            );
                          }
                        })}
                    </tr>
                  );
                }
              }
            }
            return rows;
          })()}
        </div>
      );
    });
    return renderData;
  };

  if (GS?.isVisible)
    return (
      <React.Fragment>
        <div className={styles.partyIdentity}>
          <table className={styles.hitsTable}>
            <thead>{getAlertViewHitsHeading(GS)}</thead>
            <tbody>{getAlertViewHitsData(GS)}</tbody>
          </table>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
            <p className={styles.hints}>
              <b>*</b> These fields are only applicable for individuals
            </p>
            <p
              className={styles.hints}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span role="img" aria-label="lock" style={{ marginTop: '-6px' }}>
                <LockFilled style={{ color: '#1890ff', fontSize: '10px' }} />
              </span>
              <span>Hard Rule Parameter</span>
            </p>
          </div>
          {showRelatedPartiesModal?.watchlistId &&
            showRelatedPartiesModal?.visible && (
              <RelatedPartyModal
                visible={showRelatedPartiesModal?.visible}
                onCancel={() =>
                  setShowRelatedPartiesModal((prev) => ({
                    ...prev,
                    visible: false
                  }))
                }
                hitDetails={showRelatedPartiesModal?.hitDetails}
                watchlistId={showRelatedPartiesModal?.watchlistId}
              />
            )}
        </div>
      </React.Fragment>
    );
  else return null;
};

NSAlertDetailHits.propTypes = {
  GS: PropTypes.object,
  data: PropTypes.object,
  apiActions: PropTypes.object,
  hitsUserFeedback: PropTypes.object,
  onSelectForComparision: PropTypes.func,
  alertLevels: PropTypes.array
};

export default NSAlertDetailHits;
